import { useCallback, useEffect, useState } from 'react';
import { ScrollText, CheckCircle2 } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import nurseryAccountsService from '../../services/nurseryAccounts';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { errorMessage } from '../../utils/apiResponse';
import { useOrgTimezone, formatDateTimeInTz } from '../../utils/timezone';
import { TRADING_TERMS_MAX_LENGTH, type TradingTerms as Terms } from '../../types/nurseryAccounts';

/**
 * /accounts/terms — the organisation's own terms of trade with its customers,
 * printed after every invoice and statement it downloads.
 *
 * These are the nursery's conditions of sale, not FactreeTech's Terms. An
 * Accounts Admin writes them; any accounts seat reads them. Nothing here
 * judges what they say.
 */

// A starting point, not advice. Every clause is the organisation's to change.
const EXAMPLE = `1. Payment. Unless otherwise agreed in writing, payment is due 30 days from the end of the month of invoice. Prices are in Australian dollars and exclude GST unless stated.

2. Late payment. We may suspend supply to an account that is overdue, and recover reasonable costs of collecting overdue amounts.

3. Title. Ownership of the goods passes to the customer only when they have been paid for in full. Until then the customer holds them for us, and we may register our interest under the Personal Property Securities Act 2009 (Cth).

4. Risk. Risk in the goods passes to the customer on delivery or collection.

5. Claims. Shortages, damage or incorrect goods must be reported within 48 hours of delivery, quoting the invoice number. Plants must be cared for appropriately after delivery.

6. Returns. Goods are not accepted for return without our prior agreement.

7. Consumer law. Nothing in these terms excludes any guarantee or right under the Australian Consumer Law that cannot lawfully be excluded.`;

function TradingTerms() {
  const tz = useOrgTimezone();
  const { hasAccess: isAdmin } = useModuleLicense('accounts', 'admin');
  const [saved, setSaved] = useState<Terms | null>(null);
  const [heading, setHeading] = useState('');
  const [terms, setTerms] = useState('');
  const [onInvoices, setOnInvoices] = useState(true);
  const [onStatements, setOnStatements] = useState(true);
  const [onCreditNotes, setOnCreditNotes] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  const apply = (t: Terms) => {
    setSaved(t);
    setHeading(t.heading);
    setTerms(t.terms);
    setOnInvoices(t.on_invoices);
    setOnStatements(t.on_statements);
    setOnCreditNotes(t.on_credit_notes);
  };

  const load = useCallback(async () => {
    try {
      const res = await nurseryAccountsService.getTradingTerms();
      if (res.success && res.data) apply(res.data);
      else setError(errorMessage(res, 'The terms of trade could not be read'));
    } catch (e) {
      setError(errorMessage(e, 'The terms of trade could not be read'));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const dirty = saved !== null && (
    heading !== saved.heading || terms !== saved.terms || onInvoices !== saved.on_invoices
    || onStatements !== saved.on_statements || onCreditNotes !== saved.on_credit_notes
  );

  const save = async () => {
    setBusy(true);
    setError(null);
    setJustSaved(false);
    try {
      const res = await nurseryAccountsService.saveTradingTerms({
        heading, terms, on_invoices: onInvoices, on_statements: onStatements, on_credit_notes: onCreditNotes,
      });
      if (res.success && res.data) {
        apply(res.data);
        setJustSaved(true);
      } else setError(errorMessage(res, 'The terms of trade could not be saved'));
    } catch (e) {
      setError(errorMessage(e, 'The terms of trade could not be saved'));
    }
    setBusy(false);
  };

  const blank = !terms.trim();

  return (
    <>
      <PageHeader
        title="Terms of trade"
        subtitle="Your conditions of sale, printed after your invoices, statements and credit notes"
        icon={<ScrollText className="h-6 w-6 text-white" />}
      />
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">{error}</div>}

        {!isAdmin && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-900 break-words">
            You hold the Accounts User seat. An Accounts Admin changes the terms of trade.
          </div>
        )}

        <section className="bg-white border border-neutral-200 rounded-xl p-4 space-y-2 text-sm text-neutral-700 break-words">
          <p>
            Whatever you write here is printed under your heading after every invoice, statement and credit note
            downloaded from FactreeTech. They start on a new page, so printed double-sided they fall on the back of
            a one-page invoice, in two columns and in small print sized to fit that one page.
          </p>
          <p>
            Put each clause on its own line. A line that starts with a clause number (<span className="font-mono">1.</span>,{' '}
            <span className="font-mono">13.2</span>, <span className="font-mono">(a)</span>) is printed with the number
            standing out to the left, indented to its level. Copying straight out of a PDF usually breaks a clause
            into many short lines, so paste into a plain text editor first and join them up.
          </p>
          <p>
            Invoices sent to your customers from Xero carry the terms set on your Xero invoice template, not these.
            These are your terms with your customers. FactreeTech does not check them; have them reviewed by your
            own adviser.
          </p>
        </section>

        {saved && (
          <fieldset disabled={!isAdmin || busy} className="bg-white border border-neutral-200 rounded-xl p-4 space-y-4 min-w-0">
            <div className="space-y-1">
              <label htmlFor="trading-terms-heading" className="block text-sm font-medium text-neutral-800">Heading</label>
              <input
                id="trading-terms-heading"
                type="text"
                value={heading}
                maxLength={80}
                placeholder="Terms of trade"
                onChange={(e) => { setHeading(e.target.value); setJustSaved(false); }}
                className="w-full max-w-sm rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500 disabled:bg-neutral-50"
              />
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2">
              <label htmlFor="trading-terms" className="text-base font-semibold text-neutral-900">Your terms</label>
              {isAdmin && blank && (
                <Button variant="outline" size="sm" onClick={() => setTerms(EXAMPLE)}>Start from an example</Button>
              )}
            </div>
            <textarea
              id="trading-terms"
              value={terms}
              onChange={(e) => { setTerms(e.target.value); setJustSaved(false); }}
              maxLength={TRADING_TERMS_MAX_LENGTH}
              rows={16}
              placeholder="None yet. Nothing is printed until there is something here."
              className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm font-mono leading-relaxed focus:border-primary-500 focus:ring-primary-500 disabled:bg-neutral-50"
            />
            <p className="text-xs text-neutral-500 break-words">
              {terms.length.toLocaleString('en-AU')} of {TRADING_TERMS_MAX_LENGTH.toLocaleString('en-AU')} characters.
              Each line is a paragraph; a blank line adds a little space.
              {terms === EXAMPLE && ' This is an example only: change it to suit your business before you save it.'}
            </p>

            <div className="space-y-2">
              <span className="block text-sm font-medium text-neutral-800">Print them on</span>
              <label className="flex items-start gap-2 text-sm text-neutral-700">
                <input type="checkbox" className="mt-0.5" checked={onInvoices}
                  onChange={(e) => { setOnInvoices(e.target.checked); setJustSaved(false); }} />
                <span className="break-words">Invoices, and the invoice copies downloaded for an account</span>
              </label>
              <label className="flex items-start gap-2 text-sm text-neutral-700">
                <input type="checkbox" className="mt-0.5" checked={onStatements}
                  onChange={(e) => { setOnStatements(e.target.checked); setJustSaved(false); }} />
                <span className="break-words">Statements</span>
              </label>
              <label className="flex items-start gap-2 text-sm text-neutral-700">
                <input type="checkbox" className="mt-0.5" checked={onCreditNotes}
                  onChange={(e) => { setOnCreditNotes(e.target.checked); setJustSaved(false); }} />
                <span className="break-words">Credit notes</span>
              </label>
            </div>

            {isAdmin && (
              <div className="flex flex-wrap items-center gap-3">
                <Button onClick={save} disabled={!dirty || busy}>{busy ? 'Saving…' : 'Save'}</Button>
                {dirty && <Button variant="outline" onClick={() => apply(saved)} disabled={busy}>Undo changes</Button>}
                {justSaved && !dirty && (
                  <span className="flex items-center gap-1 text-sm text-green-800">
                    <CheckCircle2 className="h-4 w-4" /> Saved
                  </span>
                )}
              </div>
            )}

            {saved.updated_at && (
              <p className="text-xs text-neutral-500 break-words">
                Last changed {formatDateTimeInTz(saved.updated_at, tz)}{saved.updated_by_name ? ` by ${saved.updated_by_name}` : ''}.
              </p>
            )}
          </fieldset>
        )}
      </div>
    </>
  );
}

export default TradingTerms;
